import { useState, useEffect } from "react";
import { Trash2 } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useTeam } from "../context/TeamContext";
import { useAuth } from "../auth/AuthContext";
import { api } from "../api/clientInstance";
import { apiErrorMessage } from "../utils/apiError";
import NewMatchFlow from "./match/NewMatchFlow";
import StatsBar from "./common/StatsBar";
import "./DashboardPage.css";

function PendingMatchesSection({ matches, onWatch, onDismiss }) {
  const { t } = useTranslation();
  if (!matches.length) return null;
  return (
    <div className="dashboard-pending">
      <p className="dashboard-pending-label">{t("dashboard.pendingMatches")}</p>
      {matches.map((m) => (
        <div key={m.match_id} className="dashboard-pending-item">
          <span className="dashboard-pending-challenger">{m.opponent}</span>
          <button className="btn-primary dashboard-pending-btn" onClick={() => onWatch(m.match_id)}>
            {t("dashboard.watch")}
          </button>
          <button className="btn-ghost dashboard-pending-btn" onClick={() => onDismiss(m.match_id)}>
            <Trash2 size={15} />
          </button>
        </div>
      ))}
    </div>
  );
}

const TYPE_KEY = { friendly: "dashboard.typeFriendly", random: "dashboard.typeRandom", cup: "dashboard.typeCup" };

function MatchHistorySection({ history }) {
  const { t } = useTranslation();
  return (
    <div className="dashboard-history">
      <p className="dashboard-pending-label">{t("dashboard.history")}</p>
      {history.length === 0 ? (
        <p className="dashboard-history-empty">{t("dashboard.historyEmpty")}</p>
      ) : (
        <table className="dashboard-history-table">
          <tbody>
            {history.map((h, i) => {
              const win  = h.goals_for > h.goals_against;
              const draw = h.goals_for === h.goals_against;
              return (
                <tr key={i} className="dashboard-history-row">
                  <td className="dashboard-history-teams">{h.my_team} – {h.opponent}</td>
                  <td className={`dashboard-history-score dashboard-history-score--${win ? "win" : draw ? "draw" : "loss"}`}>
                    {h.goals_for}–{h.goals_against}
                  </td>
                  <td className="dashboard-history-type">{t(TYPE_KEY[h.type] ?? "dashboard.typeFriendly")}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      )}
    </div>
  );
}

function CupFixturesSection({ onMatchPlayed }) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [fixtures, setFixtures] = useState([]);
  const [playing, setPlaying]   = useState(null);
  const [error, setError]       = useState(null);

  useEffect(() => {
    api("/cup/fixtures/pending").then(setFixtures).catch(() => {});
  }, []);

  if (fixtures.length === 0) return null;

  const handlePlay = async (fixtureId) => {
    setPlaying(fixtureId);
    setError(null);
    try {
      const result = await api(`/game/cup/${fixtureId}`, { method: "POST" });
      setFixtures((prev) => prev.filter((f) => f.fixture_id !== fixtureId));
      onMatchPlayed?.();
      navigate(`/match/${result.match_id}`);
    } catch (e) {
      setError(apiErrorMessage(e, t));
    } finally {
      setPlaying(null);
    }
  };

  return (
    <div className="dashboard-pending">
      <p className="dashboard-pending-label">{t("dashboard.cupFixtures")}</p>
      {fixtures.map((f) => (
        <div key={f.fixture_id} className="dashboard-pending-item">
          <span className="dashboard-pending-challenger">
            <span className="dashboard-cup-name">{f.cup_name}</span>
            {" · "}
            {f.opponent}
            {f.opponent_manager && <span className="coach-sub">{f.opponent_manager}</span>}
          </span>
          <button
            className="btn-primary dashboard-pending-btn"
            onClick={() => handlePlay(f.fixture_id)}
            disabled={playing === f.fixture_id}
          >
            {playing === f.fixture_id ? "…" : t("dashboard.play")}
          </button>
        </div>
      ))}
      {error && <p className="dashboard-error">{error}</p>}
    </div>
  );
}

function ReadinessHint({ readiness }) {
  const { t } = useTranslation();
  if (readiness.ready) return null;
  const issues = [];
  if (readiness.no_team)         issues.push(t("dashboard.hintNoTeam"));
  if (readiness.no_starter)      issues.push(t("dashboard.hintNoStarter"));
  if (readiness.missing_tactics) issues.push(t("dashboard.hintMissingTactics", { count: readiness.missing_tactics }));
  return (
    <div className="dashboard-hint">
      {issues.map((msg, i) => <p key={i}>⚠ {msg}</p>)}
    </div>
  );
}

function Leaderboard({ userCountry }) {
  const { t }   = useTranslation();
  const [tab, setTab]         = useState("global");
  const [rows, setRows]       = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    const url = tab === "country" && userCountry
      ? `/game/leaderboard?country=${userCountry}`
      : "/game/leaderboard";
    api(url)
      .then(setRows)
      .catch(() => setRows([]))
      .finally(() => setLoading(false));
  }, [tab, userCountry]);

  return (
    <div className="leaderboard">
      <p className="dashboard-card-label">{t("dashboard.lbTitle")}</p>
      <div className="leaderboard-tabs">
        <button className={`leaderboard-tab ${tab === "global" ? "leaderboard-tab--active" : ""}`} onClick={() => setTab("global")}>
          {t("dashboard.lbGlobal")}
        </button>
        {userCountry && (
          <button className={`leaderboard-tab ${tab === "country" ? "leaderboard-tab--active" : ""}`} onClick={() => setTab("country")}>
            {userCountry}
          </button>
        )}
      </div>

      {loading ? (
        <p className="leaderboard-empty">{t("dashboard.loading")}</p>
      ) : rows.length === 0 ? (
        <p className="leaderboard-empty">{t("dashboard.lbEmpty")}</p>
      ) : (
        <table className="leaderboard-table">
          <tbody>
            {rows.map((r) => (
              <tr key={r.rank} className={r.is_me ? "leaderboard-row leaderboard-row--me" : "leaderboard-row"}>
                <td className="lb-rank">{r.rank}</td>
                <td className="lb-team">
                  {r.team}
                  {r.manager && <span className="coach-sub">{r.manager}</span>}
                </td>
                {tab === "global" && <td className="lb-country">{r.country ?? ""}</td>}
                <td className="lb-fill" aria-hidden="true" />
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
}


export default function DashboardPage() {
  const { t } = useTranslation();
  const { user } = useAuth();
  const { team, loading: teamLoading, readiness } = useTeam();
  const navigate = useNavigate();
  const [pendingMatches, setPendingMatches] = useState([]);
  const [matchHistory,   setMatchHistory]   = useState([]);
  const [stats,          setStats]          = useState(null);

  useEffect(() => {
    if (!team) return;
    api("/game/matches/pending").then(setPendingMatches).catch(() => {});
    api("/game/matches/history").then(setMatchHistory).catch(() => {});
    api("/user/stats").then(setStats).catch(() => {});
  }, [team]);

  if (teamLoading) return <p className="dashboard-loading">{t("dashboard.loading")}</p>;

  return (
    <div className="dashboard-bg" style={{ backgroundImage: `linear-gradient(rgba(0,0,0,0.6), rgba(0,0,0,0.6)), url(${process.env.PUBLIC_URL}/images/field_bg.webp)` }}>
    <div className="dashboard">
      <div className="dashboard-main">
        {stats && (
          <div className="dashboard-stats-card">
            <StatsBar stats={stats} size="md" />
          </div>
        )}

        <ReadinessHint readiness={readiness} />

        {!team && (
          <button className="btn-primary" onClick={() => navigate("/team/create")}>
            {t("dashboard.createTeam")}
          </button>
        )}

        <div className="dashboard-action-centre">
          <NewMatchFlow readiness={readiness} />
        </div>

        <PendingMatchesSection
          matches={pendingMatches}
          onWatch={(id) => navigate(`/match/${id}`)}
          onDismiss={(id) => {
            api(`/game/match/${id}`, { method: "DELETE" }).catch(() => {});
            setPendingMatches((prev) => prev.filter((m) => m.match_id !== id));
          }}
        />

        <CupFixturesSection
          onMatchPlayed={() => api("/game/matches/history").then(setMatchHistory).catch(() => {})}
        />

        <MatchHistorySection history={matchHistory} />
      </div>

      <aside className="dashboard-sidebar">
        <Leaderboard userCountry={user?.country} />
      </aside>
    </div>
    </div>
  );
}
